import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { calculateScores } from '../services/scoringService';
import { shareResults, getCurrentUser } from '../services/api';
import { forceInfo, interpretations } from '../data/surveyQuestions';
import InvitePeersModal from './InvitePeersModal';
import ForceModal from './ForceModal';
import ChartInfoModal from './ChartInfoModal';
import CreativeWorkScoreModal from './CreativeWorkScoreModal';
import ShareResultsModal from './ShareResultsModal';
import { useNavigate } from 'react-router-dom';

function ResultsPage({ responses, onRestart }) {
  const svgRef = useRef();
  const donutRef = useRef();
  const downloadBtnRef = useRef();
  const shareBtnRef = useRef();
  const navigate = useNavigate();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedForce, setSelectedForce] = useState(null);
  const [showChartInfo, setShowChartInfo] = useState(false);
  const [showScoreInfo, setShowScoreInfo] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const results = calculateScores(responses);

  // Force descriptions for modal
  const forceDescriptions = {
    s: 'It all starts with a new thought, insight, or creative solution. But an idea alone creates zero outcome. You must move it forward. Creative work becomes powerful when it flows toward Impact',
    a: 'Capture ideas the moment they appear. A napkin sketch. A voice memo. A quick bullet point. If it\'s not noted, it\'s as good as lost. Notes anchor your ideas in memory. Ideas rarely arrive when it\'s convenient. Capture thoughts instantly - without breaking your flow.',
    c: 'Most creative progress happens with others. Share your thoughts - in a meeting, a message, or a brainstorm - to build momentum and refine the idea. Most people just broadcast ideas. High performers share to align. The shift is simple: Don\'t just push information out. Try to adapt, clarify, and connect ideas to the right audience.',
    g: 'Every idea needs direction. Decide what to do next - and in what order. Be the decider, letting only the right actions through. Sometimes the right move is to wait. Timing is also a decision - knowing when to act and when to wait. Most people use Decision process to get recommendations. High performers use it to improve judgment.',
    f: 'This is where impact begins. Nothing matters more than taking action. The smallest step forward beats the most brilliant, unexecuted idea. High performers focus to accelerate execution. The shift is simple: Don\'t stop at ideas. Turn decisions into output - faster, cleaner, and at higher quality.',
    p: 'Creative work doesn\'t follow a perfect schedule. That\'s why reminders matter - to revisit ideas, follow up on decisions, or nudge a task back to life. Keep your hand on the pulse of the flow - the what, when, and who, always at the right time. Most people just set reminders. High performers maintain momentum. The shift is simple: Don\'t just track tasks. Monitor progress, surface drift, and protect focus.',
  };
  
  // Check if top 2 forces have the same score
  const getTopForces = () => {
    const forces = ['s', 'a', 'c', 'g', 'f', 'p'];
    const sortedForces = forces.map(force => ({
      force,
      score: results.scores[force]
    })).sort((a, b) => b.score - a.score);
    
    const topTwo = sortedForces.slice(0, 2);
    const hasTie = topTwo[0].score === topTwo[1].score;
    
    return {
      hasTie,
      topForces: hasTie ? topTwo : [topTwo[0]],
      sortedForces
    };
  };
  
  const topForcesInfo = getTopForces();
  
  // Calculate dynamic interpretations based on actual scores
  const getDynamicInterpretation = () => {
    const forceLabels = {
      s: 'Spark',
      a: 'Anchor',
      c: 'Connector',
      g: 'Decider',
      f: 'Builder',
      p: 'Pulse'
    };
    const forceDescriptions = {
      s: 'Innovation & Imagination',
      a: 'Organization & Structure',
      c: 'Communication & Collaboration',
      g: 'Decision-Making & Prioritization',
      f: 'Execution & Results',
      p: 'Follow-Up & Accountability'
    };
    
    // Sort forces by score
    const sortedForces = topForcesInfo.sortedForces.map(item => ({
      ...item,
      label: forceLabels[item.force],
      description: forceDescriptions[item.force]
    }));
    
    // Get top 2 and bottom 2
    const topTwo = sortedForces.slice(0, 2);
    const bottomTwo = sortedForces.slice(-2).reverse();
    
    return {
      strength: `Your strongest forces are ${topTwo[0].label} (${topTwo[0].score}) and ${topTwo[1].label} (${topTwo[1].score}). You excel at ${topTwo[0].description.toLowerCase()} and ${topTwo[1].description.toLowerCase()}. These are your natural strengths that help you create impact.`,
      growth: `Your development opportunities are in ${bottomTwo[0].label} (${bottomTwo[0].score}) and ${bottomTwo[1].label} (${bottomTwo[1].score}). Focusing on ${bottomTwo[0].description.toLowerCase()} and ${bottomTwo[1].description.toLowerCase()} could help you become more well-rounded and effective.`,
      teams: interpretations[results.nickname]?.teams || 'You bring unique value to your team through your combination of strengths.'
    };
  };
  
  const forceInterpretation = getDynamicInterpretation();
  
  // Get display name for profile
  const getProfileDisplayName = () => {
    if (topForcesInfo.hasTie) {
      const forceNames = {
        s: 'The Spark',
        a: 'The Anchor',
        c: 'The Connector',
        g: 'The Decider',
        f: 'The Builder',
        p: 'The Pulse'
      };
      return `${forceNames[topForcesInfo.topForces[0].force]} & ${forceNames[topForcesInfo.topForces[1].force]}`;
    }
    return results.nickname;
  };

  const handleDownloadPDF = async () => {
    try {
      // Get the main results container
      const element = document.querySelector('.results-container');
      if (!element) {
        console.error('Results container not found');
        return;
      }

      // Show loading state
      const button = downloadBtnRef.current;
      const originalText = button.textContent;
      button.textContent = 'Generating PDF...';
      button.disabled = true;

      // Capture the element as canvas
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Create PDF from canvas
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight;
      let position = 0;

      // Add pages as needed for full content
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      // Download the PDF
      pdf.save(`Creative-Footprint-${results.nickname}.pdf`);

      // Restore button state
      button.textContent = originalText;
      button.disabled = false;
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
      const button = downloadBtnRef.current;
      button.textContent = 'Download PDF Report';
      button.disabled = false;
    }
  };

  const handleShareResults = async () => {
    try {
      // Get the spider chart SVG
      const chartElement = svgRef.current;
      if (!chartElement) {
        alert('Spider chart not found');
        return;
      }

      // Show loading state
      const button = shareBtnRef.current;
      const originalText = button.textContent || 'Share Results';
      button.textContent = 'Preparing to Share...';
      button.disabled = true;

      // Capture spider chart as image
      const chartCanvas = await html2canvas(chartElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const chartImageBase64 = chartCanvas.toDataURL('image/png').split(',')[1]; // Get base64 without data URI prefix

      // Generate full page PDF for attachment
      const element = document.querySelector('.results-container');
      const fullCanvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = fullCanvas.toDataURL('image/png');
      const imgWidth = 210;
      const imgHeight = (fullCanvas.height * imgWidth) / fullCanvas.width;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      // Get PDF as base64
      const pdfBase64 = pdf.output('datauristring').split(',')[1]; // Get base64 without data URI prefix

      // Get recipient email from user
      const recipientEmail = prompt('Enter recipient email address:');
      if (!recipientEmail) {
        button.textContent = originalText;
        button.disabled = false;
        return;
      }

      // Send to backend
      button.textContent = 'Sending Email...';
      console.log('🚀 Sending share request to backend...');
      console.log('   Recipient:', recipientEmail);
      console.log('   Profile:', results.nickname);
      console.log('   Chart image size:', chartImageBase64.length);
      console.log('   PDF size:', pdfBase64.length);
      
      await shareResults(recipientEmail, chartImageBase64, pdfBase64, results.nickname);

      // Show success message
      alert(`✅ Email sent successfully to ${recipientEmail}!\n\nThe email includes:\n- Spider chart image\n- Complete PDF report\n- Link to Wheel of Work`);
      
      button.textContent = originalText;
      button.disabled = false;

    } catch (error) {
      console.error('❌ Error sharing results:', error);
      console.error('   Error message:', error.message);
      console.error('   Full error:', error);
      alert(`Failed to send email: ${error.message || 'Please try again.'}`);
      const button = shareBtnRef.current;
      button.textContent = 'Share Results';
      button.disabled = false;
    }
  };

  useEffect(() => {
    if (!svgRef.current) return;

    // Create spider chart
    const width = Math.min(500, window.innerWidth - 40);
    const height = width;
    const radius = (width / 2) * 0.7;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('display', 'block')
      .style('margin', '0 auto')
      .style('width', '100%')
      .style('max-width', '500px')
      .style('height', 'auto');

    const g = svg.append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    // Force data in order: S, A, C, G, F, P
    const forces = ['s', 'a', 'c', 'g', 'f', 'p'];
    const forceLabels = ['Spark', 'Anchor', 'Connector', 'Decider', 'Builder', 'Pulse'];
    const data = forces.map((force, i) => ({
      axis: forceLabels[i],
      value: results.scores[force],
      force: force
    }));

    const angleSlice = (Math.PI * 2) / forces.length;

    // Draw grid circles
    [20, 40, 60, 80, 100].forEach((level) => {
      const levelRadius = (radius / 100) * level;
      g.append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', levelRadius)
        .attr('fill', 'none')
        .attr('stroke', '#e2e8f0')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3');

      g.append('text')
        .attr('x', 0)
        .attr('y', -levelRadius)
        .attr('dy', '-0.3em')
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('fill', '#999')
        .text(`${level}`);
    });

    // Draw radial lines
    data.forEach((d, i) => {
      const angle = angleSlice * i - Math.PI / 2;
      const x = radius * Math.cos(angle);
      const y = radius * Math.sin(angle);

      g.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', x)
        .attr('y2', y)
        .attr('stroke', '#e2e8f0')
        .attr('stroke-width', 1);
    });

    // Create line generator
    const lineGenerator = d3.line()
      .x((d, i) => {
        const angle = angleSlice * i - Math.PI / 2;
        return ((d.value / 100) * radius) * Math.cos(angle);
      })
      .y((d, i) => {
        const angle = angleSlice * i - Math.PI / 2;
        return ((d.value / 100) * radius) * Math.sin(angle);
      });

    // Draw filled area
    g.append('path')
      .attr('d', lineGenerator(data) + 'Z')
      .attr('fill', '#bfdbfe')
      .attr('fill-opacity', 0.3)
      .attr('stroke', '#3b82f6')
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 1);

    // Draw nodes and labels
    data.forEach((d, i) => {
      const angle = angleSlice * i - Math.PI / 2;
      const x = (radius * 1.15) * Math.cos(angle);
      const y = (radius * 1.15) * Math.sin(angle);
      const nodeX = ((d.value / 100) * radius) * Math.cos(angle);
      const nodeY = ((d.value / 100) * radius) * Math.sin(angle);

      // Draw node
      g.append('circle')
        .attr('cx', nodeX)
        .attr('cy', nodeY)
        .attr('r', 5)
        .attr('fill', forceInfo[d.force].color)
        .attr('stroke', 'white')
        .attr('stroke-width', 2);

      // Add force label with initials instead of icon
      g.append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('fill', forceInfo[d.force].color)
        .text(d.force.toUpperCase());

      // Add force name and score
      g.append('text')
        .attr('x', x)
        .attr('y', y + 25)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .attr('fill', '#333')
        .text(d.axis);

      g.append('text')
        .attr('x', x)
        .attr('y', y + 40)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('fill', forceInfo[d.force].color)
        .attr('font-weight', 'bold')
        .text(`${d.value} points`);
    });

  }, [results]);

  // Donut chart for Thinking vs Doing balance
  useEffect(() => {
    if (!donutRef.current) return;

    const width = 200;
    const height = 200;
    const radius = Math.min(width, height) / 2;
    const innerRadius = radius * 0.6;

    // Clear previous content
    d3.select(donutRef.current).selectAll("*").remove();

    const svg = d3.select(donutRef.current)
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    // Define gradients for Thinking (blue to teal) and Doing (mint to green)
    const defs = svg.append('defs');

    const thinkingGradient = defs.append('linearGradient')
      .attr('id', 'thinking-gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');
    
    thinkingGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#0066a1'); // primary-blue
    
    thinkingGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#4ECDC4'); // anchor

    const doingGradient = defs.append('linearGradient')
      .attr('id', 'doing-gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');
    
    doingGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#95E1D3'); // decider
    
    doingGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#10b981'); // success-color

    const data = [
      { label: 'Thinking', value: results.thinkingForces, gradient: 'url(#thinking-gradient)' },
      { label: 'Doing', value: results.doingForces, gradient: 'url(#doing-gradient)' }
    ];

    const pie = d3.pie()
      .value(d => d.value)
      .sort(null);

    const arc = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(radius);

    const arcs = g.selectAll('.arc')
      .data(pie(data))
      .enter()
      .append('g')
      .attr('class', 'arc');

    arcs.append('path')
      .attr('d', arc)
      .attr('fill', d => d.data.gradient)
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    // Add percentage labels
    arcs.append('text')
      .attr('transform', d => {
        const [x, y] = arc.centroid(d);
        return `translate(${x}, ${y})`;
      })
      .attr('text-anchor', 'middle')
      .attr('font-size', '16px')
      .attr('font-weight', 'bold')
      .attr('fill', 'white')
      .text(d => `${d.data.value}%`);

    // Add profile type text in the center of the donut
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '16px')
      .attr('font-weight', '600')
      .attr('fill', '#0066a1')
      .text(results.profileType);

  }, [results]);

  return (
    <div className="results-container">
      <div className="results-header">
        <h1>Your Creative Work Footprint</h1>
        <p className="results-subtitle">Based on your responses to the survey</p>
      </div>

      <div className="results-content">
        {/* Dominant Profile Card */}
        <div className="profile-card dominant-profile">
          {topForcesInfo.hasTie ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', margin: '0 auto 20px' }}>
              <img 
                src={forceInfo[topForcesInfo.topForces[0].force].icon} 
                alt={forceInfo[topForcesInfo.topForces[0].force].name} 
                onClick={() => setSelectedForce(topForcesInfo.topForces[0].force)}
                style={{ width: '60px', height: '60px', display: 'inline-block', margin: 0, cursor: 'pointer', transition: 'transform 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              />
              <img 
                src={forceInfo[topForcesInfo.topForces[1].force].icon} 
                alt={forceInfo[topForcesInfo.topForces[1].force].name}
                onClick={() => setSelectedForce(topForcesInfo.topForces[1].force)}
                style={{ width: '60px', height: '60px', display: 'inline-block', margin: 0, cursor: 'pointer', transition: 'transform 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              />
            </div>
          ) : (
            <img 
              src={forceInfo[results.dominantForce].icon} 
              alt={results.nickname} 
              className="profile-icon"
              onClick={() => setSelectedForce(results.dominantForce)}
              style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            />
          )}
          <h2 className="profile-nickname">{getProfileDisplayName()}</h2>
          
          <p className="profile-subtitle">{forceInterpretation.description}</p>
          
          {/* Score and Balance Section - Responsive Layout */}
          <div className="score-balance-section">
            {/* Creative Work Score */}
            <div className="creative-work-section">
              <div className="creative-work-score" onClick={() => setShowScoreInfo(true)} style={{ cursor: 'pointer' }}>
                <h4>Creative Work Score</h4>
                <div className="score-circle">
                  <span className="score-value">{results.creativeWorkScore}</span>
                  <span className="score-label">/100</span>
                </div>
              </div>
            </div>
            
            {/* Thinking vs Doing Balance - Donut Chart */}
            <div className="balance-chart-section" onClick={() => setShowChartInfo(true)} style={{ cursor: 'pointer' }}>
              <h4 style={{ marginBottom: '15px', fontSize: '1.1rem', color: '#333' }}>Thinking vs Doing Balance</h4>
              <svg ref={donutRef}></svg>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', marginTop: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '20px', height: '12px', background: 'linear-gradient(90deg, #0066a1, #4ECDC4)', borderRadius: '2px' }}></div>
                  <span style={{ fontSize: '14px', color: '#666' }}>Thinking ({results.thinkingForces}%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '20px', height: '12px', background: 'linear-gradient(90deg, #95E1D3, #10b981)', borderRadius: '2px' }}></div>
                  <span style={{ fontSize: '14px', color: '#666' }}>Doing ({results.doingForces}%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Spider Chart */}
        <div className="chart-section">
          <h3>Your Force Scores</h3>
          <svg ref={svgRef}></svg>
        </div>

        {/* Detailed Interpretation */}
        <div className="interpretation-section">
          <h3>What This Means for You</h3>
          
          <div className="interpretation-grid">
            <div className="interpretation-item strength">
              <h4>Your Strength</h4>
              <p>{forceInterpretation.strength}</p>
            </div>

            <div className="interpretation-item growth">
              <h4>Growth Opportunity</h4>
              <p>{forceInterpretation.growth}</p>
            </div>

            <div className="interpretation-item teams">
              <h4>In Teams</h4>
              <p>{forceInterpretation.teams}</p>
            </div>
          </div>

          <div className="full-interpretation">
            <h4>Full Profile</h4>
            <p>{forceInterpretation.description}</p>
          </div>
        </div>

        {/* All Forces Breakdown */}
        <div className="forces-breakdown">
          <h3>All Force Scores</h3>
          <div className="forces-grid">
            {['s', 'a', 'c', 'g', 'f', 'p'].map((force) => {
              const totalPoints = results.scores.s + results.scores.a + results.scores.c + results.scores.g + results.scores.f + results.scores.p;
              const forcePercentage = Math.round((results.scores[force] / totalPoints) * 100);
              return (
                <div key={force} className="force-score-card" onClick={() => setSelectedForce(force)} style={{ cursor: 'pointer' }}>
                  <div className="force-header">
                    <img src={forceInfo[force].icon} alt={forceInfo[force].name} className="force-icon-img" />
                    <h4>{forceInfo[force].name}</h4>
                  </div>
                  <div className="force-score-bar">
                    <div 
                      className="force-score-fill"
                      style={{ 
                        width: `${results.scores[force]}%`,
                        backgroundColor: forceInfo[force].color
                      }}
                    ></div>
                  </div>
                  <p className="force-score-value">{results.scores[force]} <span className="force-score-unit">points ({forcePercentage}%)</span></p>
                </div>
              );
            })}
          </div>
          <div className="overall-force-score">
            <p>Combined Force Score: <strong>{Math.round((results.scores.s + results.scores.a + results.scores.c + results.scores.g + results.scores.f + results.scores.p) / 6)}</strong> / 100 average</p>
          </div>
        </div>

        {/* Next Steps */}
        <div className="next-steps">
          <h3>What's Next?</h3>
          <div className="next-steps-content">
            <p>Now that you understand your Creative Work profile, you can:</p>
            <ul>
              <li>Share your results with your team</li>
              <li>Download a personalized PDF report</li>
              <li>Explore how to leverage your strengths</li>
              <li>Identify development areas</li>
              <li>Retake the assessment in 30 days to track growth</li>
            </ul>
          </div>
        </div>

        {/* AI Chat Window Placeholder */}
        <div className="ai-assistant-section">
          <h4 style={{ margin: '0 0 15px 0', color: '#0066a1', fontSize: '1.1rem' }}>AI Assistant</h4>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '15px', 
            borderRadius: '6px',
            marginBottom: '15px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
              Coming soon: Chat with an AI assistant to explore your results and get personalized recommendations.
            </p>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            borderTop: '1px solid #e2e8f0', 
            paddingTop: '15px' 
          }}>
            <input 
              type="text" 
              placeholder="Ask about your results..." 
              disabled
              style={{ 
                flex: 1, 
                padding: '10px', 
                borderRadius: '6px', 
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                backgroundColor: '#f1f5f9',
                cursor: 'not-allowed'
              }}
            />
            <button 
              disabled
              style={{ 
                padding: '10px 20px', 
                borderRadius: '6px', 
                border: 'none',
                backgroundColor: '#cbd5e1',
                color: 'white',
                cursor: 'not-allowed',
                fontWeight: '600'
              }}
            >
              Send
            </button>
          </div>
        </div>
      </div>

      <div className="results-actions">
        <button className="btn btn-primary" onClick={onRestart}>
          Take Survey Again
        </button>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            const user = getCurrentUser();
            if (user && user.id) {
              navigate(`/comparison/${user.id}`);
            }
          }}
        >
          Get Feedback
        </button>
        <button 
          className="btn btn-secondary" 
          onClick={() => setShowInviteModal(true)}
          style={{ display: 'none' }}
        >
          Compare with Others
        </button>
        <button ref={downloadBtnRef} className="btn btn-secondary" onClick={handleDownloadPDF}>
          Download PDF Report
        </button>
        <button ref={shareBtnRef} className="btn btn-secondary" onClick={() => setShowShareModal(true)} style={{ display: 'none' }}>
          Share Results
        </button>
      </div>

      <InvitePeersModal 
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />

      {/* Force Modal */}
      {selectedForce && (
        <ForceModal 
          force={selectedForce}
          forceInfo={forceInfo}
          content={forceDescriptions[selectedForce]}
          onClose={() => setSelectedForce(null)}
        />
      )}

      {/* Chart Info Modal */}
      {showChartInfo && (
        <ChartInfoModal onClose={() => setShowChartInfo(false)} />
      )}

      {/* Creative Work Score Modal */}
      {showScoreInfo && (
        <CreativeWorkScoreModal score={results.creativeWorkScore} onClose={() => setShowScoreInfo(false)} />
      )}

      {/* Share Results Modal */}
      <ShareResultsModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        results={results}
      />
    </div>
  );
}

export default ResultsPage;
